import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import { useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import DraggableComponent from '../DraggableComponent';
import { AppDispatch, AppState } from 'store';
import {
  getLastSelectionText,
  getLanguageCode,
  getLastAssignmentInfo,
  getLastAssignmentVariable,
  onRevertAssignment,
} from '../../services';
import { configure } from '../../store/states/ConfigurationSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppAuthContext } from '../../services/Contexts';
import { updateAutoSelectedDialog } from '../../store/states/ApplicationSettingsSlice';
import type {
  IExtendedConfigureResponse,
  IAutoSelectedAssignmentsDialogProps,
} from '../../../types';
import { VariableAssignmentsTable } from '../VariableAssignmentsTable';

/**
 * Dialog component to display new assignments in a scrollable table without accordion
 * @param {IAutoSelectedAssignmentsDialogProps} props - Component props
 * @returns {JSX.Element} New Assignment Dialog component
 */
export const AutoSelectedAssignmentsDialog: React.FC<IAutoSelectedAssignmentsDialogProps> = ({
  assignedByRuleDifferences,
  onAccept,
}) => {
  const { t } = useTranslation();
  const token: string = useContext(AppAuthContext);

  // State for the "Do Not show again" checkbox
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  // Local state to control dialog visibility
  const [isOpen, setIsOpen] = useState(true);

  const configuration = useSelector((state: AppState) => state.configuration);
  const applicationSettings = useSelector((state: AppState) => state.applicationSettings);

  const dispatch = useDispatch<AppDispatch>();

  // Handle checkbox change - only updates Redux state, doesn't close dialog
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setDoNotShowAgain(checked);
  };

  // Handle dialog close
  const handleClose = () => {
    dispatch(updateAutoSelectedDialog({ showAutoSelections: !doNotShowAgain }));
    setIsOpen(false);
  };

  // Handle accept button - closes dialog and calls parent handler
  const handleAccept = () => {
    handleClose();
    onAccept();
  };

  // Handle revert button - closes dialog and calls revert logic
  const handleRevert = () => {
    handleClose();
    onRevert(configuration, token, dispatch);
  };

  // Don't render if dialog should be closed or no differences
  if (!isOpen || !assignedByRuleDifferences || assignedByRuleDifferences.count === 0) {
    return null;
  }

  const onRevert = (
    configuration: IExtendedConfigureResponse,
    token: string,
    dispatch: AppDispatch
  ) => {
    const request = onRevertAssignment(configuration);
    if (request) {
      dispatch(configure({ request: request, token: token, language: getLanguageCode() }));
    }
  };

  const lastAssignmentVariable = getLastAssignmentVariable(configuration);
  const lastAssignmentValue = getLastAssignmentInfo(configuration);

  const classSelector = Number(applicationSettings?.pageSize?.isExtraSmall);
  const flexDirection = ['flex-row', 'flex-column'];
  const alignSelf = ['', 'align-start'];
  const maxWidth = ['mw-25', 'mw-100'];

  return (
    <Dialog
      className="AutoSelectedAssignmentDialog-root"
      open={isOpen}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { className: 'AutoSelectedAssignmentDialog-paper' } }}
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle className="header d-flex align-center cursor-move h-auto">
        {t('newAssignment.title')} ({assignedByRuleDifferences.count})
      </DialogTitle>
      <DialogContent className="dialogContent">
        <Box className={`selection ${flexDirection[classSelector]} ${alignSelf[classSelector]}`}>
          <Box className={`selectionLabel  ${maxWidth[classSelector]}`}>
            {`${t('labels.selection')}`} &nbsp;
          </Box>

          <Box className="selectionValue">
            {lastAssignmentVariable &&
              getLastSelectionText(lastAssignmentVariable, lastAssignmentValue, true, true)}
          </Box>
        </Box>
        <div className="auto-included-message">
          <Trans
            i18nKey="newAssignment.autoIncludedMessage"
            values={{ count: assignedByRuleDifferences.count }}
            components={{ 1: <strong /> }}
          />
        </div>
        <div className="scrollableContent">
          {Object.entries(assignedByRuleDifferences.differences).map(([productKey, sections]) => (
            <VariableAssignmentsTable
              key={productKey}
              productKey={productKey}
              sections={sections}
            />
          ))}
        </div>
      </DialogContent>
      <DialogActions className="footer">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div className="checkbox-container">
            <FormControlLabel
              control={
                <Checkbox
                  checked={doNotShowAgain}
                  onChange={handleCheckboxChange}
                  color="primary"
                />
              }
              label={t('newAssignment.doNotShowAgain')}
            />
          </div>
          <div className="button-container">
            <Button
              className="text-capitalize"
              color="info"
              variant="contained"
              onClick={handleAccept}
            >
              {t('button.accept')}
            </Button>
            <Button
              className="text-capitalize"
              color="primary"
              variant="contained"
              onClick={handleRevert}
            >
              {t('button.cancel')}
            </Button>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};
